import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  CheckboxInput,
  FormContainer,
  NextButton,
  Option,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { requiredArrayOfStrings } from "~/utils/validations";

type Values = {
  pregnancyBackground: string[];
  pregnancyOtherDetails: string;
};

const validationSchema = Yup.object({
  pregnancyBackground: requiredArrayOfStrings,
  pregnancyOtherDetails: Yup.string().test(
    "pregnancyBackground",
    'This field is required when "Other" is selected.',
    function (value) {
      const { pregnancyBackground } = this.parent;
      return pregnancyBackground && pregnancyBackground.includes("Other")
        ? Boolean(value)
        : true;
    },
  ),
});

const options: Option[] = [
  {
    text: "No",
    value: "No",
  },
  {
    text: "Yes, and I’ve experienced loss",
    value: "Yes, and I’ve experienced loss",
  },
  {
    text: "Yes, I’ve been pregnant and given birth to a child (or children)",
    value: "Yes, and given birth to a child (or children)",
  },
  {
    text: "I have child(ren) through surrogacy, adoption, or my partner",
    value: "I have child(ren) through surrogacy, adoption, or my partner",
  },
  {
    text: "Other",
    value: "Other",
  },
];

export const PregnancyBackgroundPage: FC = () => {
  const { t } = useTranslation();
  const { pregnancyBackground, pregnancyOtherDetails } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();

  const initialValues: Values = {
    pregnancyBackground: pregnancyBackground || [],
    pregnancyOtherDetails: pregnancyOtherDetails || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const showAdditionalQuestion =
    formik?.values?.pregnancyBackground?.includes("Other");

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt question={t("onboarding.pregnancyBackgroundPage.question")} />

          <CheckboxInput
            id="pregnancyBackground"
            options={options}
            defaultValue={pregnancyBackground}
          />
        </QuestionContainer>

        {showAdditionalQuestion && (
          <QuestionContainer my={5}>
            <Prompt
              question={t(
                "onboarding.pregnancyBackgroundPage.additionalQuestion.question",
              )}
            />

            <StringInput
              id="pregnancyOtherDetails"
              placeholder={t(
                "onboarding.pregnancyBackgroundPage.additionalQuestion.placeholder",
              )}
            />
          </QuestionContainer>
        )}

        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
